import { useState } from 'react'

import { LinkedText } from '@/components/chat/LinkedText'
import { env } from '@/config/env'

/**
 * Renders an answer that may contain photo markers.
 *
 * Part of the removable personal layer (see `src/app/companion/README.md`). The model is
 * told it can write `[[photo:some-slug]]` on its own line; everything else it writes is
 * ordinary text. So this is an identity transform for every answer that has no marker —
 * which is every GitHub answer — and that is the whole reason it is safe to wrap the
 * existing text node in it.
 *
 * The slug is resolved server-side against a scan of the photo folder, so a made-up slug
 * 404s rather than reaching for a path.
 */
const PHOTO_MARKER = /\[\[photo:([a-zA-Z0-9][a-zA-Z0-9._-]*)\]\]/g

export function CompanionAnswer({ text }: { text: string }) {
  // `split` with a capturing group interleaves the captures: [text, slug, text, slug, …].
  const parts = text.split(PHOTO_MARKER)
  // Text parts go through LinkedText, the core renderer, so links stay clickable here too.
  if (parts.length === 1) return <LinkedText text={text} />

  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          <CompanionPhoto key={`photo-${index}-${part}`} slug={part} />
        ) : (
          <LinkedText key={`text-${index}`} text={part} />
        ),
      )}
    </>
  )
}

function CompanionPhoto({ slug }: { slug: string }) {
  const [failed, setFailed] = useState(false)
  const label = slug.replace(/[-_.]+/g, ' ')

  // A picture that has been deleted, or a slug the model invented, must not leave a broken
  // image icon sitting in the middle of a memory.
  if (failed) {
    return <span className="text-xs text-ink-faint">[photo “{label}” is not available]</span>
  }

  return (
    <figure className="my-3">
      <img
        src={`${env.apiBaseUrl}/api/companion/photos/${encodeURIComponent(slug)}`}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        className="max-h-96 w-auto max-w-full rounded-md border border-line object-contain"
      />
    </figure>
  )
}
